import { useEffect, useMemo, useRef, useState } from 'react';
import LoadingSpinner from '../../../../../shared/components/loading-spinner/LoadingSpinner';
import { ICohortVessel } from '../../../../common/cohort/cohortTypes';
import CohortDefinition from '../../../../common/cohort/components/characteristic/CohortDefinition';
import { TPhasePercentages, TPhasePercentagesItem, TPolygonCalls } from '../../../kpiTypes';
import PolygonCallsOperationalPhaseChart from './PolygonCallsOperationalPhaseChart';
import ExpandableContainer from '../../../../../shared/components/expandable-container/ExpandableContainer';

interface IPortActivityProps {
	vessel: ICohortVessel;
	minSize: number;
	maxSize: number;
	cohortVessels: ICohortVessel[];
	vesselPolygonCalls: TPolygonCalls | null;
	vesselCohortPhasePercentagesOfPolygonCall: TPhasePercentages | null;
	minAge: number;
	maxAge: number;
}

const PortActivity: React.FC<IPortActivityProps> = ({ ...props }) => {
	const ref = useRef<HTMLDivElement>(null);

	const [panelOffset, setPanelOffset] = useState<{ top: number; left: number }>();

	const cohortVesselInfo = props.cohortVessels.length === 1 ? props.cohortVessels[0] : props.vessel;

	useEffect(() => {
		const updatePanelSize = () => {
			if (ref.current) {
				const { offsetTop, offsetLeft } = ref.current;
				setPanelOffset({ top: offsetTop, left: offsetLeft });
			}
		};

		// Initial measurement
		updatePanelSize();

		// Update on window resize
		window.addEventListener('resize', updatePanelSize);
		return () => {
			window.removeEventListener('resize', updatePanelSize);
		};
	}, []);

	const polygonCallsPhases = useMemo(() => {
		if (!props.vesselPolygonCalls?.dataList) return [];

		// Sum up each of the fields across all rows
		const totals = props.vesselPolygonCalls.dataList.reduce(
			(acc, item) => {
				acc.totalTimeInPort += item.totalTimeInPort;
				acc.timeAtAnchor += item.timeAtAnchor;
				acc.timeMoored += item.timeMoored;
				acc.timeManeouvring += item.timeManeouvring;
				acc.timeUnclassified += item.timeUnclassified;
				return acc;
			},
			{
				totalTimeInPort: 0,
				timeAtAnchor: 0,
				timeMoored: 0,
				timeManeouvring: 0,
				timeUnclassified: 0,
			},
		);

		// Calculate percentages for each phase
		const phases: TPhasePercentagesItem[] = [
			{
				operationalPhase: 'At Anchor',
				countOfPhase: totals.timeAtAnchor,
				percentOfPhase: (totals.timeAtAnchor / totals.totalTimeInPort) * 100,
			},
			{
				operationalPhase: 'Moored',
				countOfPhase: totals.timeMoored,
				percentOfPhase: (totals.timeMoored / totals.totalTimeInPort) * 100,
			},
			{
				operationalPhase: 'Maneouvring',
				countOfPhase: totals.timeManeouvring,
				percentOfPhase: (totals.timeManeouvring / totals.totalTimeInPort) * 100,
			},
			{
				operationalPhase: 'Unclassified',
				countOfPhase: totals.timeUnclassified,
				percentOfPhase: (totals.timeUnclassified / totals.totalTimeInPort) * 100,
			},
		];

		return phases;
	}, [props.vesselPolygonCalls?.dataList]);

	return (
		<div ref={ref} className="flex flex-row gap-4 w-full h-full">
			<div className="flex flex-col gap-2 w-full">
				<div className="flex-none">
					<CohortDefinition
						title={props.vessel.name}
						shipsNumber={props.cohortVessels.length}
						size={props.vessel.size}
						sizeUnit={props.vessel.sizeUnit}
						age={props.vessel.age}
						isCohort={false}
					/>
				</div>
				<div className="flex-auto border border-msi-darkgrey-color-300 rounded">
					{!props.vesselPolygonCalls || props.vesselPolygonCalls?.loading ? (
						<LoadingSpinner size="md" spinnerClassName="p-2" />
					) : (
						<ExpandableContainer panelOffsets={panelOffset}>
							<PolygonCallsOperationalPhaseChart chartKey={props.vessel.imoNumber} phasePercentagesData={polygonCallsPhases} />
						</ExpandableContainer>
					)}
				</div>
			</div>
			<div className="flex flex-col gap-2 w-full">
				<div className="flex-none">
					<CohortDefinition
						title="Cohort (Average By Vessel)"
						minSize={props.minSize}
						maxSize={props.maxSize}
						shipsNumber={props.cohortVessels.length}
						size={cohortVesselInfo.size}
						sizeUnit={cohortVesselInfo.sizeUnit}
						age={cohortVesselInfo.age}
						minAge={props.minAge}
						maxAge={props.maxAge}
						isCohort={true}
					/>
				</div>
				<div className="flex-auto w-full h-full border border-msi-darkgrey-color-300 rounded">
					{!props.vesselCohortPhasePercentagesOfPolygonCall || props.vesselCohortPhasePercentagesOfPolygonCall?.loading ? (
						<LoadingSpinner size="md" spinnerClassName="p-2" />
					) : (
						<ExpandableContainer panelOffsets={panelOffset}>
							<PolygonCallsOperationalPhaseChart
								chartKey={props.vessel.imoNumber}
								phasePercentagesData={props.vesselCohortPhasePercentagesOfPolygonCall.dataList!}
							/>
						</ExpandableContainer>
					)}
				</div>
			</div>
		</div>
	);
};

export default PortActivity;
